/**
 * src/utils/roleMatrix.js
 *
 * Identity module — the Role Matrix derivation.
 *
 * Tryggr describes the same person through three independent role systems:
 *
 *   1. Job roles        — `state.roleDefinitions`, assigned to EMPLOYEES via
 *                         `employee.assignedRoles[]` (what the person does, and
 *                         which responsibilities they have acknowledged).
 *   2. Platform roles   — `state.seasuiteRoles`, assigned to USERS via
 *                         `user.seasuiteRoleId` (which modules they can open).
 *   3. Governance roles — Azure SQL via `governance-roles-crud`, assigned to
 *                         USERS by `roleKey` (what they are accountable for in
 *                         the risk lifecycle).
 *
 * Employees and users are two different populations with no stored link — they
 * join by canonical email only, through the one shared `emailIdentity` helper.
 * This module reconciles the three and reports the gaps; it writes nothing.
 *
 * IDENTITY BOUNDARY (IDN-B-001): assignment is not competence. Nothing derived
 * here may be read as a statement that a person is competent or fulfils a role.
 * The only claims made are "assigned", "acknowledged" and "acknowledged an
 * out-of-date version" — ISO 27001:2022 clause 5.3 evidence, never clause 7.2.
 */

import { realEmployees } from './sampleRecords.js';
import { canonicaliseEmail } from './emailIdentity.js';
import { getEmployeeRoleAckStatus } from './roleAcknowledgement.js';
import { readImplication } from './roleImplications.js';

/** Governance data availability. Drives IDN-B-004 (unknown is never "none"). */
export const GOVERNANCE_STATUS = {
  OK: 'ok', // loaded — governance gaps are trustworthy
  DISABLED: 'disabled', // governanceRbacV1 feature flag is off for this tenant
  UNAVAILABLE: 'unavailable', // not loaded yet, or the read failed
};

/**
 * Every gap the matrix can report. `blocking` gaps stop the person from being
 * governable at all; `info` gaps are things the administrator should look at.
 */
export const GAPS = {
  NO_ACCOUNT: {
    code: 'no-account',
    label: 'No platform account',
    detail: 'This employee has no user account, so they can hold no platform or governance role.',
    kind: 'blocking',
  },
  NO_EMPLOYEE_RECORD: {
    code: 'no-employee-record',
    label: 'No employee record',
    detail:
      'This account has no matching employee record, so no job role or acknowledgement can be evidenced against it.',
    kind: 'blocking',
  },
  NO_JOB_ROLE: {
    code: 'no-job-role',
    label: 'No job role',
    detail: 'No role definition is assigned, so no responsibilities have been communicated.',
    kind: 'blocking',
  },
  ORPHANED_JOB_ROLE: {
    code: 'orphaned-job-role',
    label: 'Orphaned assignment',
    detail: 'An assignment points at a role definition that no longer exists.',
    kind: 'blocking',
  },
  ACK_PENDING: {
    code: 'ack-pending',
    label: 'Not acknowledged',
    detail: 'An assigned role has never been acknowledged by the employee.',
    kind: 'info',
  },
  ACK_STALE: {
    code: 'ack-stale',
    label: 'Re-acknowledgement due',
    detail: 'The role definition changed after the employee last acknowledged it.',
    kind: 'info',
  },
  NO_PLATFORM_ROLE: {
    code: 'no-platform-role',
    label: 'No platform role',
    detail: 'This account falls back to its base permissions — module access is not governed.',
    kind: 'info',
  },
  GOVERNANCE_WITHOUT_JOB_ROLE: {
    code: 'governance-without-job-role',
    label: 'Accountable, no defined role',
    detail:
      'This person holds a governance role in the risk lifecycle, but no job role defines that responsibility.',
    kind: 'blocking',
  },
  GRANT_GAP: {
    code: 'grant-gap',
    label: 'Missing required access',
    detail:
      'A job role this person holds requires access they do not have, so they cannot do the job it describes.',
    kind: 'blocking',
  },
  DRIFT: {
    code: 'drift',
    label: 'Unexplained access',
    detail:
      'This person holds access that no job role of theirs requires. Either a job role is missing, or the access is.',
    kind: 'blocking',
  },
  REVOCATION_DUE: {
    code: 'revocation-due',
    label: 'Access to revoke',
    detail:
      'Access that outlived the job role which required it, or that belongs to someone who has left.',
    kind: 'blocking',
  },
  REQUIREMENT_CONFLICT: {
    code: 'requirement-conflict',
    label: 'Conflicting requirements',
    detail:
      'Two job roles require different platform roles. An account can hold only one, so somebody has to decide which.',
    kind: 'blocking',
  },
  NOT_EVALUABLE: {
    code: 'not-evaluable',
    label: 'Cannot be evaluated',
    detail:
      'Required access cannot be compared for this person, because there is no account to compare it against.',
    kind: 'info',
  },
  UNFILLED_ROLE: {
    code: 'unfilled-role',
    label: 'Unfilled',
    detail: 'This role is defined but nobody is assigned to it.',
    kind: 'blocking',
  },
};

const ROLE_KINDS = {
  job: 'Job role',
  platform: 'Platform role',
  governance: 'Governance role',
};

function nameOfEmployee(e) {
  return e?.fullName || e?.name || e?.email || '';
}

function nameOfUser(u) {
  return u?.displayName || u?.name || u?.email || '';
}

function userIdOf(u) {
  return u?.id || u?.userId || null;
}

function indexByEmail(list, getEmail) {
  const map = new Map();
  for (const item of list || []) {
    const key = canonicaliseEmail(getEmail(item) || '');
    if (!key) continue;
    // First writer wins — a duplicate email is itself surfaced as a gap by the
    // count on the summary rather than silently overwriting the first record.
    if (!map.has(key)) map.set(key, item);
  }
  return map;
}

/**
 * Employment states that mean the person should no longer hold access. Mirrors
 * the `status !== 'departed' && status !== 'offboarding'` convention already
 * used by src/utils/resolveReaders.js — the same two values, read the same way,
 * so the product has one idea of who has left.
 */
const LEAVER_STATUSES = new Set(['offboarding', 'departed']);

function isLeaver(employee) {
  return !!employee && LEAVER_STATUSES.has(employee.status);
}

/**
 * Compare what a person's job roles REQUIRE against what they actually hold.
 *
 * Three findings come out of the same comparison, and the difference between
 * them is the explanation they carry, which is what makes them actionable:
 *
 *   grant-gap      required, not held        → they cannot do the job
 *   revocation-due held, formerly required   → access outlived its reason
 *   drift          held, never required      → nothing explains this access
 *
 * `revocation-due` is deliberately checked before `drift`: both describe
 * unexplained access, but one of them knows WHY, and an administrator acting on
 * "the role that required this was removed in March" is in a different position
 * from one acting on "no idea".
 *
 * FAIL-SAFE (IDN-B-004): governance findings are produced only when the
 * governance store actually loaded. When it did not, what the person holds is
 * unknown — not empty — so claiming a gap or a drift would be an invention.
 * The platform half is unaffected, and is still compared.
 */
function deriveAccessFindings({
  employee,
  user,
  activeRoleDefs,
  formerRoleDefs,
  heldPlatformRoleId,
  heldGovernanceKeys,
  governanceTrusted,
}) {
  // Without an account there is nothing to compare against: the person holds no
  // platform or governance access at all, and the NO_ACCOUNT gap already says
  // so. Reporting "missing required access" on top would be noise, not a
  // finding — the fix is an account, not a grant.
  if (!user) {
    return { findings: [], platformRoleConflict: null, evaluable: false };
  }

  // What the person's CURRENT job roles require, and which role required it.
  const requiredPlatform = new Map(); // platformRoleId → [jobRoleName]
  const requiredGovernance = new Map(); // roleKey → [jobRoleName]
  for (const rd of activeRoleDefs) {
    const imp = readImplication(rd);
    if (imp.platformRoleId) {
      if (!requiredPlatform.has(imp.platformRoleId)) requiredPlatform.set(imp.platformRoleId, []);
      requiredPlatform.get(imp.platformRoleId).push(rd.name);
    }
    for (const key of imp.governanceRoleKeys) {
      if (!requiredGovernance.has(key)) requiredGovernance.set(key, []);
      requiredGovernance.get(key).push(rd.name);
    }
  }

  // What job roles they USED to hold required — the explanation behind
  // revocation-due. A role still held actively is not a former requirement.
  const formerlyRequiredPlatform = new Map();
  const formerlyRequiredGovernance = new Map();
  for (const rd of formerRoleDefs) {
    const imp = readImplication(rd);
    if (imp.platformRoleId && !requiredPlatform.has(imp.platformRoleId)) {
      if (!formerlyRequiredPlatform.has(imp.platformRoleId)) {
        formerlyRequiredPlatform.set(imp.platformRoleId, []);
      }
      formerlyRequiredPlatform.get(imp.platformRoleId).push(rd.name);
    }
    for (const key of imp.governanceRoleKeys) {
      if (requiredGovernance.has(key)) continue;
      if (!formerlyRequiredGovernance.has(key)) formerlyRequiredGovernance.set(key, []);
      formerlyRequiredGovernance.get(key).push(rd.name);
    }
  }

  const leaver = isLeaver(employee);
  const findings = [];
  let platformRoleConflict = null;

  const add = (kind, system, id, name, because) =>
    findings.push({ kind, system, id, name, because });

  // ── Platform role ────────────────────────────────────────────────────────
  // seasuiteRoleId is singular, so "required but a different one is held" is
  // both a gap and an unexplained holding — and two job roles requiring two
  // different roles is a conflict no automatic rule may resolve.
  const platformSatisfied = heldPlatformRoleId && requiredPlatform.has(heldPlatformRoleId);
  for (const [roleId, byRoles] of requiredPlatform) {
    if (roleId === heldPlatformRoleId) continue;
    add('grant-gap', 'platform', roleId, roleId, `required by ${byRoles.join(', ')}`);
  }
  if (requiredPlatform.size > 1) {
    // Never resolved automatically, and never by "most permissive wins": the
    // administrator decides which requirement is real. Reported, not fixed.
    platformRoleConflict = {
      jobRoleNames: [...requiredPlatform.values()].flat(),
      platformRoleIds: [...requiredPlatform.keys()],
    };
  }
  if (heldPlatformRoleId && !platformSatisfied) {
    if (leaver) {
      add(
        'revocation-due',
        'platform',
        heldPlatformRoleId,
        heldPlatformRoleId,
        'the person has left'
      );
    } else if (formerlyRequiredPlatform.has(heldPlatformRoleId)) {
      add(
        'revocation-due',
        'platform',
        heldPlatformRoleId,
        heldPlatformRoleId,
        `only ${formerlyRequiredPlatform.get(heldPlatformRoleId).join(', ')} required it, and it is no longer assigned`
      );
    } else {
      add('drift', 'platform', heldPlatformRoleId, heldPlatformRoleId, 'no job role requires it');
    }
  } else if (heldPlatformRoleId && platformSatisfied && leaver) {
    add(
      'revocation-due',
      'platform',
      heldPlatformRoleId,
      heldPlatformRoleId,
      'the person has left'
    );
  }

  // ── Governance roles — only when the store actually loaded ───────────────
  if (governanceTrusted) {
    const held = new Set(heldGovernanceKeys);
    for (const [key, byRoles] of requiredGovernance) {
      if (held.has(key)) continue;
      add('grant-gap', 'governance', key, key, `required by ${byRoles.join(', ')}`);
    }
    for (const key of held) {
      const required = requiredGovernance.has(key);
      if (required && !leaver) continue;
      if (leaver) {
        add('revocation-due', 'governance', key, key, 'the person has left');
      } else if (formerlyRequiredGovernance.has(key)) {
        add(
          'revocation-due',
          'governance',
          key,
          key,
          `only ${formerlyRequiredGovernance.get(key).join(', ')} required it, and it is no longer assigned`
        );
      } else {
        add('drift', 'governance', key, key, 'no job role requires it');
      }
    }
  }

  return { findings, platformRoleConflict, evaluable: true };
}

/**
 * Reconcile the three role systems into one person-centred and one role-centred
 * view.
 *
 * @param {object} input
 * @param {Array}  input.employees        state.employees
 * @param {Array}  input.users            tenant users
 * @param {Array}  input.roleDefinitions  state.roleDefinitions
 * @param {Array}  input.seasuiteRoles    state.seasuiteRoles
 * @param {object} input.governance       { status, defs, assignments }
 * @returns {{ people: Array, roles: Array, summary: object }}
 */
export function buildRoleMatrix({
  employees: allEmployees = [],
  users = [],
  roleDefinitions = [],
  seasuiteRoles = [],
  governance = { status: GOVERNANCE_STATUS.UNAVAILABLE, defs: [], assignments: [] },
} = {}) {
  // A sample person holds no access and raises no finding (GH #7870).
  const employees = realEmployees(allEmployees);
  const govStatus = governance?.status || GOVERNANCE_STATUS.UNAVAILABLE;
  const govTrusted = govStatus === GOVERNANCE_STATUS.OK;
  const govDefs = govTrusted ? governance.defs || [] : [];
  const govAssignments = govTrusted ? governance.assignments || [] : [];

  const employeesByEmail = indexByEmail(employees, (e) => e.email);
  const usersByEmail = indexByEmail(users, (u) => u.email);

  const govByUserId = new Map();
  for (const a of govAssignments) {
    if (!a?.userId) continue;
    if (!govByUserId.has(a.userId)) govByUserId.set(a.userId, []);
    govByUserId.get(a.userId).push(a);
  }
  const govDefByKey = new Map(govDefs.map((d) => [d.roleKey, d]));
  const platformRoleById = new Map((seasuiteRoles || []).map((r) => [r.id, r]));
  const roleDefById = new Map((roleDefinitions || []).map((r) => [r.id, r]));

  // ── One row per person: the union of both populations, joined by email ────
  const emails = new Set([...employeesByEmail.keys(), ...usersByEmail.keys()]);
  const people = [];

  const pushPerson = (employee, user, emailKey) => {
    const ack = employee
      ? getEmployeeRoleAckStatus(employee, roleDefinitions)
      : { assigned: 0, acknowledged: 0, pending: 0, reackNeeded: 0, orphaned: 0, items: [] };

    const jobRoles = ack.items
      .filter((i) => i.definition)
      .map((i) => ({
        id: i.definition.id,
        name: i.definition.name,
        status: i.status, // 'acknowledged' | 'pending' | 'reack-needed'
      }));

    const uid = userIdOf(user);
    const platformRole = user?.seasuiteRoleId
      ? platformRoleById.get(user.seasuiteRoleId) || null
      : null;

    const governanceRoles = uid
      ? (govByUserId.get(uid) || []).map((a) => ({
          roleKey: a.roleKey,
          name: govDefByKey.get(a.roleKey)?.displayName || a.roleKey,
          assignedAt: a.assignedAt || null,
        }))
      : [];

    // IDNA-02 — compare what the person's job roles REQUIRE against what they
    // hold. Former requirements come from the `unassignedAt`-stamped entries
    // EMP-B-002 preserves as evidence, which is what lets a revocation name the
    // role it outlived instead of reading as unexplained drift.
    const activeRoleDefs = ack.items
      .filter((i) => i.definition && i.status !== undefined)
      .map((i) => i.definition);
    const formerRoleDefs = (employee?.assignedRoles || [])
      .filter((a) => a?.unassignedAt)
      .map((a) => roleDefById.get(a.roleDefId))
      .filter(Boolean);

    const access = deriveAccessFindings({
      employee,
      user,
      activeRoleDefs,
      formerRoleDefs,
      heldPlatformRoleId: platformRole?.id || null,
      heldGovernanceKeys: governanceRoles.map((r) => r.roleKey),
      governanceTrusted: govTrusted,
    });

    const gaps = [];
    if (employee && !user) gaps.push(GAPS.NO_ACCOUNT);
    if (user && !employee) gaps.push(GAPS.NO_EMPLOYEE_RECORD);
    if (employee && jobRoles.length === 0 && ack.orphaned === 0) gaps.push(GAPS.NO_JOB_ROLE);
    if (ack.orphaned > 0) gaps.push(GAPS.ORPHANED_JOB_ROLE);
    if (ack.pending > 0) gaps.push(GAPS.ACK_PENDING);
    if (ack.reackNeeded > 0) gaps.push(GAPS.ACK_STALE);
    if (user && !platformRole) gaps.push(GAPS.NO_PLATFORM_ROLE);
    // IDN-B-004: only claim this gap when governance data is actually loaded —
    // an empty assignment list from a failed read is "unknown", never "none".
    if (govTrusted && governanceRoles.length > 0 && jobRoles.length === 0) {
      gaps.push(GAPS.GOVERNANCE_WITHOUT_JOB_ROLE);
    }
    if (access.findings.some((f) => f.kind === 'grant-gap')) gaps.push(GAPS.GRANT_GAP);
    if (access.findings.some((f) => f.kind === 'revocation-due')) gaps.push(GAPS.REVOCATION_DUE);
    if (access.findings.some((f) => f.kind === 'drift')) gaps.push(GAPS.DRIFT);
    if (access.platformRoleConflict) gaps.push(GAPS.REQUIREMENT_CONFLICT);
    // An employee with no account cannot be compared at all — said once, rather
    // than reported as "no findings", which would read as a clean result.
    if (!access.evaluable) gaps.push(GAPS.NOT_EVALUABLE);

    people.push({
      id: employee ? `emp:${employee.id}` : `usr:${uid}`,
      name: nameOfEmployee(employee) || nameOfUser(user) || emailKey,
      email: employee?.email || user?.email || emailKey,
      employeeId: employee?.id || null,
      userId: uid,
      linkStatus: employee && user ? 'linked' : employee ? 'employee-only' : 'user-only',
      jobRoles,
      jobRoleNames: jobRoles.map((r) => r.name),
      platformRole: platformRole ? { id: platformRole.id, name: platformRole.name } : null,
      platformRoleName: platformRole?.name || '',
      governanceRoles,
      governanceRoleNames: governanceRoles.map((r) => r.name),
      governanceKnown: govTrusted,
      ack,
      accessFindings: access.findings,
      grantGaps: access.findings.filter((f) => f.kind === 'grant-gap'),
      driftFindings: access.findings.filter((f) => f.kind === 'drift'),
      revocationsDue: access.findings.filter((f) => f.kind === 'revocation-due'),
      platformRoleConflict: access.platformRoleConflict,
      accessEvaluable: access.evaluable,
      isLeaver: isLeaver(employee),
      gaps,
      gapCodes: gaps.map((g) => g.code),
      blockingGapCount: gaps.filter((g) => g.kind === 'blocking').length,
    });
  };

  for (const emailKey of emails) {
    pushPerson(
      employeesByEmail.get(emailKey) || null,
      usersByEmail.get(emailKey) || null,
      emailKey
    );
  }
  // An employee with no email at all cannot be joined; it still gets a row so
  // it is visible as a gap rather than dropped from the reconciliation.
  for (const e of employees || []) {
    if (!canonicaliseEmail(e?.email || '')) pushPerson(e, null, '');
  }

  people.sort((a, b) => b.blockingGapCount - a.blockingGapCount || a.name.localeCompare(b.name));

  // ── One row per defined role, across all three systems ───────────────────
  const roles = [];

  for (const rd of roleDefinitions || []) {
    const assignees = people.filter((p) => p.jobRoles.some((r) => r.id === rd.id));
    const row = makeRoleRow('job', rd.id, rd.name, rd.description, assignees, true);
    // How many holders of this role are missing something it requires. An
    // unfilled role is one kind of problem; a role filled by people who cannot
    // actually do it is another, and only this view makes it visible.
    const imp = readImplication(rd);
    row.requires = {
      platformRoleId: imp.platformRoleId,
      platformRoleName: imp.platformRoleId
        ? platformRoleById.get(imp.platformRoleId)?.name || null
        : null,
      governanceRoleKeys: imp.governanceRoleKeys,
    };
    row.holdersMissingRequiredAccess = assignees.filter((p) =>
      p.grantGaps.some(
        (g) =>
          (g.system === 'platform' && g.id === imp.platformRoleId) ||
          (g.system === 'governance' && imp.governanceRoleKeys.includes(g.id))
      )
    ).length;
    roles.push(row);
  }
  for (const sr of seasuiteRoles || []) {
    const assignees = people.filter((p) => p.platformRole?.id === sr.id);
    roles.push(makeRoleRow('platform', sr.id, sr.name, sr.description, assignees, true));
  }
  for (const gd of govDefs) {
    const assignees = people.filter((p) => p.governanceRoles.some((r) => r.roleKey === gd.roleKey));
    roles.push(
      makeRoleRow('governance', gd.roleKey, gd.displayName, gd.description, assignees, true)
    );
  }

  roles.sort(
    (a, b) =>
      a.kind.localeCompare(b.kind) || b.gaps.length - a.gaps.length || a.name.localeCompare(b.name)
  );

  const countGap = (code) => people.filter((p) => p.gapCodes.includes(code)).length;

  return {
    people,
    roles,
    summary: {
      peopleTotal: people.length,
      linked: people.filter((p) => p.linkStatus === 'linked').length,
      employeeOnly: countGap(GAPS.NO_ACCOUNT.code),
      userOnly: countGap(GAPS.NO_EMPLOYEE_RECORD.code),
      withoutJobRole: countGap(GAPS.NO_JOB_ROLE.code),
      withoutPlatformRole: countGap(GAPS.NO_PLATFORM_ROLE.code),
      accountableWithoutJobRole: countGap(GAPS.GOVERNANCE_WITHOUT_JOB_ROLE.code),
      ackPending: countGap(GAPS.ACK_PENDING.code),
      ackStale: countGap(GAPS.ACK_STALE.code),
      grantGaps: countGap(GAPS.GRANT_GAP.code),
      drift: countGap(GAPS.DRIFT.code),
      revocationsDue: countGap(GAPS.REVOCATION_DUE.code),
      requirementConflicts: countGap(GAPS.REQUIREMENT_CONFLICT.code),
      notEvaluable: countGap(GAPS.NOT_EVALUABLE.code),
      rolesTotal: roles.length,
      rolesUnfilled: roles.filter((r) => r.gapCodes.includes(GAPS.UNFILLED_ROLE.code)).length,
      rolesWithUnderEquippedHolders: roles.filter((r) => (r.holdersMissingRequiredAccess || 0) > 0)
        .length,
      governanceStatus: govStatus,
    },
  };
}

function makeRoleRow(kind, id, name, description, assignees, defined) {
  const gaps = [];
  if (defined && assignees.length === 0) gaps.push(GAPS.UNFILLED_ROLE);
  return {
    id: `${kind}:${id}`,
    kind,
    kindLabel: ROLE_KINDS[kind],
    name: name || id,
    description: description || '',
    assignees: assignees.map((p) => ({ id: p.id, name: p.name })),
    assigneeNames: assignees.map((p) => p.name),
    assigneeCount: assignees.length,
    gaps,
    gapCodes: gaps.map((g) => g.code),
  };
}
